/**
 * reflectionPrompt.ts
 *
 * Hybrid Reflection Prompt Generator:
 * Rules dictate the timing (quiz passed, module end, capstone, short answer),
 * while lesson content (module title, lesson title, core concepts) drives the prompt content.
 */

export interface PromptContext {
    mode: 'essay_per_module' | 'short_answer_only' | 'capstone_only' | string
    subjectName?: string
    moduleTitle?: string
    moduleNumber?: number
    lessonTitle?: string
    lessonNumber?: number
    coreConcepts?: string[]
    isFaithTrack?: boolean
}

export function generateReflectionPrompt(ctx: PromptContext): string {
    const {
        mode,
        subjectName = 'this course',
        moduleTitle = ctx.moduleNumber ? `Module ${ctx.moduleNumber}` : 'this module',
        lessonTitle = ctx.lessonNumber ? `Lesson ${ctx.lessonNumber}` : 'this lesson',
        coreConcepts = [],
        isFaithTrack = false,
    } = ctx

    const validConcepts = (coreConcepts || []).map(c => c.trim()).filter(Boolean)
    const hasConcepts = validConcepts.length > 0

    // Faith / Church track customisation
    if (isFaithTrack) {
        if (hasConcepts) {
            const conceptList = validConcepts.slice(0, 3).map(c => `"${c}"`).join(', ')
            return `In your own words: what does "${moduleTitle}" teach about God and the Christian life, and what difference should it make to how you live? Draw on key concepts such as ${conceptList} from this module's lessons.`
        }
        return `In your own words: what does "${moduleTitle}" teach about God and the Christian life, and what difference should it make to how you live? Draw on the key passages and ideas from this module's lessons.`
    }

    // 1. Per-lesson short answer mode
    if (mode === 'short_answer_only') {
        if (hasConcepts) {
            const concept = validConcepts[0]
            return `In your own words: explain how "${concept}" works based on "${lessonTitle}", and give one everyday or real-world example demonstrating its impact.`
        }
        return `In your own words: summarize the key insight from "${lessonTitle}", and explain how you can apply it to a real-life situation.`
    }

    // 2. Capstone mode (course completion)
    if (mode === 'capstone_only') {
        if (hasConcepts) {
            const sample = validConcepts.slice(0, 2).map(c => `"${c}"`).join(' and ')
            return `Comprehensive Course Reflection: Looking back across "${subjectName}", analyze how core principles like ${sample} connect together, and explain how you plan to apply what you've learned in your future studies or career.`
        }
        return `Comprehensive Course Reflection: Looking back across "${subjectName}", what was the most significant concept you mastered, and how will it influence your approach to future challenges?`
    }

    // 3. Module essay mode (default: essay_per_module)
    if (hasConcepts) {
        const conceptList = validConcepts.slice(0, 3).map(c => `"${c}"`).join(', ')
        return `In your own words: reflect on the main themes of "${moduleTitle}" (including ${conceptList}). Explain why these principles matter and provide a concrete example of how they are applied in practice.`
    }
    return `In your own words: reflect on the main themes of "${moduleTitle}". Explain why these principles matter and provide a concrete example of how they are applied in practice.`
}

/**
 * Returns calibrated word minimum and maximum limits based on course level and essay mode.
 * - Foundation / Short answer: 20–60 words (kid-friendly)
 * - Intermediate: 50–150 words
 * - Advanced / Capstone: 80–250 words
 */
export function getReflectionWordLimits(
    mode?: string | null,
    curriculumTrack?: string | null
): { wordMin: number; wordMax: number } {
    // Track tiers first — age-appropriate bands for Elite Academy
    if (curriculumTrack === 'foundation') {
        return { wordMin: 20, wordMax: 60 }    // Age 8: short answers
    }
    if (curriculumTrack === 'intermediate') {
        return { wordMin: 50, wordMax: 150 }   // Age 12: module essays
    }
    if (curriculumTrack === 'advanced') {
        return { wordMin: 80, wordMax: 250 }   // Age 16+: full essays
    }
    // Mode-specific bands for non-Elite tracks
    if (mode === 'capstone_only') {
        return { wordMin: 80, wordMax: 250 }   // Capstone: substantial (any track)
    }
    if (mode === 'short_answer_only') {
        return { wordMin: 30, wordMax: 100 }   // Standard-track teens (Critical Thinking etc.)
    }
    // Default: standard per-module essays (incl. church BSc, NULL track)
    return { wordMin: 50, wordMax: 150 }
}
